<template>
  <div>
    <div v-if="source._dateDivider" class="date-divider">
      {{ $dayjs(source._dateDivider).format("DD.MM.YYYY") }}
    </div>
    <div
      class="tx-row"
      :class="{ 'tx-row--highlighted': isReferenceHighlighted }"
      :style="kindStyle"
    >
      <div class="tx-col tx-col-time">
        {{
          source.createdAt ? $dayjs(source.createdAt).format("HH:mm:ss") : "N/A"
        }}
      </div>
      <div
        class="tx-col tx-col-amount"
        :style="{ color: effectiveIsOut ? '#F56C6C' : '#67C23A' }"
      >
        <i :class="effectiveIsOut ? 'el-icon-bottom' : 'el-icon-top'"></i>
        {{ effectiveIsOut ? "-" : "+" }}{{ Math.abs(signedEffect) }}
        {{
          (source.balanceAccount &&
            source.balanceAccount.valute &&
            source.balanceAccount.valute.name) ||
            ""
        }}
        <div v-if="source.comissionAmount" class="tx-commission">
          комиссия {{ source.comissionAmount }}
        </div>
      </div>
      <div class="tx-col tx-col-desc">
        <template v-if="commentText">{{ commentText }}</template>
        <template v-else>{{ getTransactionDescr(source) }}</template>
        <div v-if="source.isHidden && source.isRevert" class="tx-note">
          Реверсирует
          <el-link
            v-if="source.reversedTransaction"
            type="primary"
            @click.native="scrollToReference(source.reversedTransaction)"
            @mouseenter.native="setHoveredReference(source.reversedTransaction)"
            @mouseleave.native="setHoveredReference(null)"
            >{{ source.reversedTransaction }}</el-link
          >
          <template v-else>N/A (легаси, ссылка не сохранена)</template>
        </div>
        <div v-if="source.isHidden && !source.isRevert" class="tx-note">
          В последующем изменена
        </div>
      </div>
      <div class="tx-col tx-col-balance">
        <template v-if="balanceBefore !== null">
          {{ balanceBefore }} → <strong>{{ source.balanceAccountAfter }}</strong>
        </template>
        <template v-else>—</template>
      </div>
      <div class="tx-col tx-col-tag">
        <el-tag v-if="kindTag" :type="kindTag.type" size="mini">{{
          kindTag.label
        }}</el-tag>
      </div>
      <div class="tx-col tx-col-actions">
        <el-link
          v-if="source.ledgerExchange && source.ledgerExchange._id && !source.ledgerExchange.isDeleted"
          :href="
            $router.resolve({
              name: 'LedgerEdit',
              params: { id: source.ledgerExchange._id }
            }).href
          "
          target="_blank"
          class="tx-action-icon"
        >
          <i class="el-icon-s-management"></i>
        </el-link>
        <el-tooltip placement="top" effect="light">
          <div slot="content">
            ID: {{ source._id }}<br />
            Создал: {{ source.creator && source.creator.login }}
            {{
              source.createdAt
                ? $dayjs(source.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "N/A"
            }}
            <template
              v-if="
                source.updatedAt &&
                  source.createdAt &&
                  !$dayjs(source.updatedAt).isSame($dayjs(source.createdAt))
              "
              ><br />Изменил:
              {{ source.creator && source.creator.login }}
              {{ $dayjs(source.updatedAt).format("DD.MM.YYYY HH:mm:ss") }}</template
            >
          </div>
          <i class="el-icon-info tx-action-icon"></i>
        </el-tooltip>
        <el-button
          type="primary"
          icon="el-icon-download"
          circle
          size="mini"
          @click="showStats"
          :loading="statsLoading"
        ></el-button>
      </div>
    </div>
  </div>
</template>
<script>
import { getLedgerTransactionsStats } from "@/api";
import { calculateStatsSummary } from "@/utils/ledgerStats";

export default {
  name: "FullBalanceTransactionCard",
  props: {
    index: {
      type: Number
    },
    source: {
      type: Object,
      required: true
    },
    // Прокинуты из fullbalance.vue через extra-props virtual-list — так
    // работает подсветка строки, на которую ссылается "Реверсирует ID" из
    // ДРУГОЙ строки ленты. Через события это не сделать: vue-virtual-scroll-
    // list не пробрасывает произвольные custom-события вложенных item-
    // компонентов наверх, только свой внутренний size-change.
    hoveredReferenceId: {
      type: String,
      default: null
    },
    setHoveredReference: {
      type: Function,
      default: () => {}
    },
    scrollToReference: {
      type: Function,
      default: () => {}
    }
  },
  data: () => ({
    statsLoading: false
  }),
  computed: {
    hasRecordedBalance() {
      return (
        this.source.balanceAccountAfter !== undefined &&
        this.source.balanceAccountAfter !== null
      );
    },
    // Точное зеркало формулы verifyBalanceAccountHistory на бэкенде
    // (helpers.js): currAmount+currComm — это и есть подписанный эффект
    // строки на баланс. Направление (source.direction) для сторно-строк уже
    // хранится ПРОТИВОПОЛОЖНЫМ направлению отменяемой транзакции — это
    // делает бэкенд при создании сторно (deleteIfNeeded/editIfNeeded), так
    // что читать его нужно НАПРЯМУЮ, без дополнительного переворота по
    // isRevert. Раньше здесь был лишний переворот, из-за которого сторно
    // показывалось с тем же знаком, что и отменяемая проводка — двойной
    // переворот (уже перевёрнутое направление переворачивалось ещё раз).
    // isRevert влияет только на знак комиссии (см. комментарий в helpers.js).
    signedEffect() {
      const amount = this.source.amount || 0;
      const commission = this.source.comissionAmount || 0;
      const isInLike = ["in", "deposit_in"].includes(this.source.direction);
      const mult = isInLike ? 1 : -1;
      const currAmount = amount * mult;
      let currComm;
      if (this.source.isRevert) {
        currComm = (isInLike && commission ? commission : -1 * commission) * mult;
      } else {
        const isOutLike = ["out", "deposit_out"].includes(this.source.direction);
        currComm = (isOutLike && commission ? commission : -1 * commission) * mult;
      }
      return parseFloat((currAmount + currComm).toFixed(12));
    },
    effectiveIsOut() {
      return this.signedEffect < 0;
    },
    balanceBefore() {
      if (!this.hasRecordedBalance) return null;
      return parseFloat(
        (this.source.balanceAccountAfter - this.signedEffect).toFixed(12)
      );
    },
    kindTag() {
      if (this.source.isReconciliation) return { type: "warning", label: "Реконсиляция" };
      if (this.source.ledgerExchange && this.source.ledgerExchange.isCorrection)
        return { type: "danger", label: "Коррекция" };
      if (this.source.isRevert && this.source.isHidden)
        return { type: "info", label: "Сторно" };
      if (this.source.isHidden && !this.source.isRevert)
        return { type: "info", label: "Изменена" };
      if (!this.hasRecordedBalance) return { type: "info", label: "Легаси" };
      return null;
    },
    commentText() {
      return (
        this.source.ledgerExchange && this.source.ledgerExchange.comment
      );
    },
    // Фон строки для "особых" видов проводки — специально НЕ зелёный/красный
    // (это уже занято знаком суммы) и разные оттенки для разных видов, чтобы
    // не сливались друг с другом. Лёгкая, а не редкая "Легаси" — фоном не
    // выделяется, иначе на старых счетах подсвечена была бы почти вся лента.
    kindStyle() {
      if (this.source.isReconciliation)
        return { background: "rgba(103, 58, 183, 0.08)", borderLeft: "3px solid #673ab7" };
      if (this.source.ledgerExchange && this.source.ledgerExchange.isCorrection)
        return { background: "rgba(32, 160, 176, 0.08)", borderLeft: "3px solid #20a0b0" };
      if (this.source.isRevert && this.source.isHidden)
        return { background: "rgba(230, 162, 60, 0.10)", borderLeft: "3px solid #e6a23c" };
      if (this.source.isHidden && !this.source.isRevert)
        return { background: "rgba(96, 98, 102, 0.08)", borderLeft: "3px solid #909399" };
      return {};
    },
    isReferenceHighlighted() {
      return !!this.hoveredReferenceId && this.hoveredReferenceId === this.source._id;
    }
  },
  methods: {
    getTransactionDescr(transaction) {
      // На этой странице всегда один и тот же счёт — повторять его название
      // в каждой строке незачем, короче: просто "пришло"/"ушло".
      return this.effectiveIsOut ? "Списание" : "Поступление";
    },
    async showStats() {
      try {
        this.statsLoading = true;
        const loadedData = await getLedgerTransactionsStats({
          query: {
            createdAt: { $gt: this.source.createdAt },
            balanceAccount: this.source.balanceAccount._id
          }
        });
        const data = loadedData && loadedData.data && loadedData.data.result;
        const summary = calculateStatsSummary(data.totalAmount);
        const calculatedBalance = parseFloat(
          (this.source.balanceAccountAfter + summary.total).toFixed(12)
        );
        const check = parseFloat(
          (
            data.balanceAccount.balance -
            (this.source.balanceAccountAfter + summary.total)
          ).toFixed(12)
        );
        const message = `Актуальный баланс: ${data.balanceAccount.balance}, расчетный баланс: ${calculatedBalance}, разница: ${check}`;
        this.$msgbox({
          title: "Проверка начиная с транзакции " + this.source._id,
          message,
          showCancelButton: false,
          confirmButtonText: "OK"
        });
      } catch (error) {
        console.log(error);
        this.$message({
          type: "error",
          message: "Произошла ошибка получения данных"
        });
      } finally {
        this.statsLoading = false;
      }
    }
  }
};
</script>
<style scoped>
.date-divider {
  text-align: center;
  color: #909399;
  font-size: 0.8rem;
  margin: 14px 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.tx-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 14px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
  transition: background 0.1s;
}
.tx-row:hover {
  background: #f5f7fa;
}
.tx-row--highlighted {
  box-shadow: inset 0 0 0 2px #409eff;
}
.tx-col {
  flex-shrink: 0;
}
.tx-col-time {
  width: 66px;
  color: #909399;
  font-size: 0.8rem;
}
.tx-col-amount {
  width: 190px;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.2;
}
.tx-col-amount i {
  margin-right: 2px;
}
.tx-commission {
  font-weight: 400;
  color: #909399;
  font-size: 0.75rem;
}
.tx-col-desc {
  flex: 1 1 auto;
  min-width: 0;
  color: #606266;
  font-size: 0.85rem;
  overflow-wrap: break-word;
}
.tx-note {
  color: #909399;
  font-size: 0.8rem;
  font-style: italic;
}
.tx-col-balance {
  width: 240px;
  color: #606266;
  font-size: 0.85rem;
  text-align: right;
}
.tx-col-tag {
  width: 90px;
}
.tx-col-actions {
  width: 100px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}
.tx-action-icon {
  color: #909399;
  font-size: 16px;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Ledger/components/FullBalance/TransactionCard.vue